import { compact } from '@lib/utils/omit'
import { extendConsole } from '@shared/logging/console'
import { isBrowser } from '@shared/util/env'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import noop from 'lodash/noop'
import type { AnnotationsMap } from 'mobx'
import {
    autorun,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx'

import { localStorageWithTTL as storage } from './storage'

/**
 * Extend this to make a store persistent.
 * That store doesn't have to be a `DataStore` for this.
 *
 * Example usage:
 *
 * ```ts
 * class MyStore extends PersistentStore {
 *   static className = 'MyStore'

 *   persistentFields = ['persistantProperty2']
 *
 *   property1 = 1
 *
 *   persistentProperty2 = 2
 *
 *  ```
 */
export abstract class PersistentStore {
    static readonly className: string

    /**
     * Lifetime of the data in minutes. If not set, the data will be stored forever
     */
    static readonly ttl?: number

    /**
     * Only these fields will be persisted and rehydrated.
     */
    static readonly persistentFields: string[] = []

    hydrated = false

    /** Internal array of disposer functions for cleanup */
    private _disposers: Array<() => void> = []

    constructor() {
        const { cls } = this
        if (!isBrowser()) {
            cls.readStorage = noop
            cls.clearStorage = noop
            cls.writeToStorage = noop
            return
        }
    }

    static get key() {
        return `storage.${this.className}`
    }

    static get logger() {
        return extendConsole(this.key)
    }

    get state() {
        const { persistentFields } = this.cls

        return Object.fromEntries(
            persistentFields.map((key) => [key, (this as any)[key]]),
        )
    }

    get className() {
        return this.cls.className
    }

    get cls() {
        return this.constructor as typeof PersistentStore
    }

    static readStorage() {
        return storage.get(this.key)
    }

    static clearStorage() {
        const { logger, key } = this
        storage.remove(key)
        logger.debug('clear')
    }

    static writeToStorage(state: any) {
        runInAction(() => {
            const value = compact(state)
            const { logger, key, ttl } = this

            if (isEmpty(value)) return

            const fromStorage = this.readStorage()
            if (isEqual(value, fromStorage)) return

            storage.set(key, value, ttl)

            if (process.env.NEXT_PUBLIC_ENABLE_STORAGE_LOGS === 'true') {
                logger.diff(
                    ttl ? `persisted for ${ttl} minutes` : 'persisted',
                    fromStorage,
                    value,
                )
            }
        })
    }

    /**
     * Cleanup method to dispose of any active subscriptions (autorun, listeners).
     * Call this when the store is no longer needed to prevent memory leaks.
     */
    dispose() {
        for (const disposer of this._disposers) {
            disposer()
        }
        this._disposers = []
    }

    /**
     * Register a disposer function to be called on dispose().
     * Used internally by makePersistentObservable.
     */
    addDisposer(disposer: () => void) {
        this._disposers.push(disposer)
    }

    clearStorage() {
        this.cls.clearStorage()
    }

    persist() {
        this.cls.writeToStorage(this.state)
    }

    rehydrate() {
        this.restore(this.cls.readStorage())
    }

    restore(source: unknown) {
        runInAction(() => {
            const {
                state: existing,
                cls: { logger },
            } = this

            if (!source) return

            if (typeof source !== 'object') {
                logger.warn('restore: source is not an object', source)
                return
            }

            const value = compact(source)

            if (isEqual(compact(existing), value)) return

            Object.assign(this, value)
            if (process.env.NEXT_PUBLIC_ENABLE_STORAGE_LOGS === 'true') {
                logger.diff('restored', existing, value)
            }
            this.hydrated = true
        })
    }
}

/**
 * Makes the object observable and persistent.
 * Stores disposers on the store instance for cleanup via store.dispose()
 */
export function makePersistentObservable<T extends PersistentStore>(
    store: T,
    overrides?: AnnotationsMap<T, PropertyKey>,
) {
    const config = {
        state: computed,
        hydrated: observable,
        ...Object.fromEntries(
            store.cls.persistentFields.map((key) => [key, observable]),
        ),
        ...overrides,
    }
    const observed = makeObservable(store, config)
    observed.rehydrate()

    // Capture autorun disposer
    const autorunDisposer = autorun(() => observed.persist())
    observed.addDisposer(autorunDisposer)

    // Capture storage listener disposer
    const listenerDisposer = storage.addListener(observed.cls.key, () =>
        observed.rehydrate(),
    )
    if (listenerDisposer) {
        observed.addDisposer(listenerDisposer)
    }

    return observed
}
